const {
    find, fill, get, remove, isUndefined,
} = require('lodash');
const { mergeInspirationTiles } = require('./merge-inspiration-tiles');

const GHOST_TILE_ITEM = {
    type: 'ghost',
};

class TileListController {
    constructor($q, $timeout, $location, $transclude, $state, serializeAsync, localStorageWrapper, moveFocus, settingsByBreakpointService, shopPageStateService, inspirationService, sortByKeys, settings) {
        this.$q = $q;
        this.$timeout = $timeout;
        this.$location = $location;
        this.$transclude = $transclude;
        this.$state = $state;
        this.localStorageWrapper = localStorageWrapper;
        this.moveFocus = moveFocus;
        this.settingsByBreakpointService = settingsByBreakpointService;
        this.shopPageStateService = shopPageStateService;
        this.inspirationService = inspirationService;
        this.sortByKeys = sortByKeys;
        this.settings = settings;

        this.loadTilesSerialized = serializeAsync(this.loadTiles, this);
    }

    $onChanges(changes) {
        this.shouldRememberSort = isUndefined(this.shouldRememberSort) ? true : this.shouldRememberSort;

        if ((changes.sortBy || changes.sortOptions) && this.sortOptions != null) {
            this.sortType = this.getSortType();
        }

        if (
            (changes.filter && !changes.filter.isFirstChange()) ||
            (changes.sortBy && !changes.sortBy.isFirstChange()) ||
            (changes.pageNumber && !changes.pageNumber.isFirstChange())
        ) {
            this.update().then(() => this.focusOnFirstTile());
        }
    }

    $onInit() {
        this.isPaginationEnabled = isUndefined(this.isPaginationEnabled) ? true : this.isPaginationEnabled;

        this.pageSize = this.settingsByBreakpointService.getSettingValueByBreakpoint('OneSite', {
            largeFormat: 'LargeFormatPageSize',
            mediumFormat: 'MediumFormatPageSize',
            smallFormat: 'SmallFormatPageSize',
        });

        if (this.sortOptions) {
            this.sortOptions.sortContext = this.sortOptions.sortContext === undefined
                ? this.sortByKeys.DEFAULT_KEY
                : this.sortOptions.sortContext;
        }

        this.items = [];
        this.shouldGetInspirationCards = this.shouldGetInspirationCards !== false;
        this.update();
    }

    getSortType() {
        // Priority: URL -> Local Storage -> First one in list
        return this.shouldRememberSort
            ? find(this.sortOptions.sortTypes, { Value: this.sortBy }) ||
                find(this.sortOptions.sortTypes, { Value: this.localStorageWrapper.get(this.sortOptions.sortContext) }) ||
                this.sortOptions.sortTypes[0]
            : find(this.sortOptions.sortTypes, { Value: this.sortBy }) ||
                this.sortOptions.sortTypes[0];
    }

    update() {
        const isAlreadyLoadingTiles = this.isLoadingTiles;
        const isAdsTakePositionPriority = this.settings.sponsoredAds.enableMerchOrAdPositionPriority;
        this.isLoadingTiles = true;

        return this.loadTilesSerialized(!isAlreadyLoadingTiles)
            .then(([{ items, totalRecordCount, aggregations }, { Cards: inspiration }]) => {
                this.items = mergeInspirationTiles(items, inspiration, { pageNumber: this.pageNumber, pageSize: this.pageSize }, isAdsTakePositionPriority);
                this.totalRecordCount = totalRecordCount;
                this.availableFilters = aggregations;
                this.filter = this.$state.params && this.$state.params.filter ? this.$state.params.filter : { filters: [] };
                this.isFilterApplied = this.$state.params && this.$state.params.filter;
                this.pushContentToAnalytics({
                    contentData: {
                        items: this.items,
                        totalRecordCount: this.totalRecordCount,
                        sortType: this.sortType,
                        inspirationCards: inspiration || [],
                        pageNumber: this.pageNumber,
                        filters: this.filter,
                    },
                });
            })
            .finally(() => {
                this.isLoadingTiles = false;
                this.shouldShowControlBar = this.totalRecordCount > 0 && (this.sortOptions || this.$transclude.isSlotFilled('topRightSlot'));
            });
    }

    loadTiles(shouldGenerateGhostTiles = true) {
        return this.$q.all([
            this.getTiles({
                pageSize: this.pageSize, pageNumber: this.pageNumber, sortType: get(this.sortType, 'Value'), filter: this.filter,
            }),
            this.shouldGetInspirationCards ? this.inspirationService.getCardsForUrl(this.$location.pathAndSearch()) : { Cards: [] },
            shouldGenerateGhostTiles ? this.generateGhostTiles() : null,
        ]);
    }

    generateGhostTiles() {
        // -1 from the page size is more aesthetically pleasing
        const amountOfGhostTiles = this.totalRecordCount ? Math.min(this.totalRecordCount, this.pageSize - 1) : this.pageSize - 1;

        // The timeout is needed to ensure that the animations run
        this.items = [];
        return this.$timeout(() => {
            this.items = fill(Array(amountOfGhostTiles), GHOST_TILE_ITEM);
        });
    }

    onSortByChanged(option) {
        this.sortBy = option.Value;
        if (this.shouldRememberSort) {
            this.localStorageWrapper.set(this.sortOptions.sortContext, this.sortBy);
        }
        this.pageNumber = 1;
        this.$state.go('.', { sortBy: option.Value, pageNumber: undefined });
    }

    onPageSelected() {
        this.shopPageStateService.resetCenterPanelScroll();
    }

    focusOnFirstTile() {
        return this.moveFocus('.tileList-tile:first-child');
    }

    onRemove(item) {
        const removeItemPromise = this.onRemoveItem ? this.onRemoveItem({ item }) : this.$q.when();

        remove(this.items, item);

        if (this.items.length === 0 && this.pageNumber > 1) {
            removeItemPromise.then(() => {
                this.$state.go('.', { pageNumber: this.pageNumber - 1 }, { reload: true });
            });
        }
    }

    get hasFilters() {
        return this.availableFilters && this.availableFilters.length > 0;
    }
}

TileListController.$inject = ['$q', '$timeout', '$location', '$transclude', '$state', 'serializeAsync', 'LocalStorageWrapper', 'MoveFocus', 'SettingsByBreakpointService', 'ShopPageStateService', 'InspirationService', 'SortByKeys', 'Settings'];

const tileListComponentDefinition = {
    bindings: {
        listTitle: '<?',
        pageNumber: '<?',
        filter: '<?',
        totalRecordCount: '<',
        sortBy: '<?',
        sortOptions: '<?',
        shouldGetInspirationCards: '<?',
        isEditing: '<?',
        onRemoveItem: '&?',
        getTiles: '&',
        isPaginationEnabled: '<?',
        shouldRememberSort: '<?',
        pushContentToAnalytics: '&',
        noResultsTerm: '<?',
        breadcrumbs: '<?',
    },
    controller: TileListController,
    transclude: {
        topRightSlot: '?topRight',
        zeroResultSlot: '?zeroResult',
        controlSlot: '?control',
    },
    template: require('./tile-list.component.html').trim(),
};

module.exports = {
    TileListController,
    tileListComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/shop/tiles/tile-list.component.js